import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { useEffect, useState, type FC } from "react";
import { useLazyRecordingS3IntakeUploadCredentialsQuery } from "../../../api/entities/recordingS3IntakeIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { CopyValueButton } from "./copyableValue.js";

const MASKED_SECRET = "••••••••••••••••••••••••••••••••••••••••";

/**
 * The S3 uploader secret access key, revealed on demand. The access key id is
 * ordinary settings data (it rides the boot payload); the secret is fetched
 * only when the teammate asks, through the UPDATE-gated reveal endpoint.
 * Hiding it (or leaving the page) calls the hook's `reset()`, which drops the
 * result from the RTK Query cache as well as from this component, so nothing
 * retains the secret once it is off screen. Read-only: the credentials are
 * provisioned by Alpharun with the bucket and cannot be changed here.
 */
export const RecordingS3IntakeUploadSecret: FC = () => {
  const [reveal, { data, isFetching, error, reset }] =
    useLazyRecordingS3IntakeUploadCredentialsQuery();
  const [isVisible, setIsVisible] = useState(false);
  const secret = isVisible ? (data?.aws_secret_access_key ?? null) : null;

  useEffect(() => reset, [reset]);

  const handleToggle = async () => {
    if (isVisible) {
      setIsVisible(false);
      reset();
      return;
    }
    try {
      // Always refetch: the value is never reused from the cache.
      await reveal(undefined, false).unwrap();
      setIsVisible(true);
    } catch {
      // `error` from the hook renders the failure below.
    }
  };

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-foreground">Secret access key</p>
      <p className="text-sm text-muted-foreground">
        Shown only on request. Revealing it is recorded.
      </p>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 rounded-md border border-border-light bg-muted px-2.5 py-1.5 font-mono text-xs break-all text-foreground">
          {secret ?? MASKED_SECRET}
        </code>
        <Can
          I={PermissionActions.UPDATE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <Button
              size={Size.SMALL}
              variant={ButtonVariant.Secondary}
              isDisabled={!allowed}
              isLoading={isFetching}
              onClick={() => void handleToggle()}
              icon={
                secret !== null ? (
                  <IconEyeOff className="size-4" />
                ) : (
                  <IconEye className="size-4" />
                )
              }
            >
              {secret !== null ? "Hide" : "Reveal"}
            </Button>
          )}
        </Can>
        {secret !== null ? (
          <CopyValueButton value={secret} label="secret access key" />
        ) : null}
      </div>
      {error ? (
        <p className="text-sm text-red-600">
          {getApiErrorMessage(error, "Couldn’t reveal the secret access key.")}
        </p>
      ) : null}
    </div>
  );
};
